<template lang="pug">
v-container(fluid)
  v-snackbar(v-model="showSnackBar" :timeout="5000" :color="snackbarColor")
    | {{ snackBarMessage }}
    v-btn(fab text @click="showSnackBar = false")
      v-icon fal fa-times-circle
  v-row(v-if="showMobileNavigationBar" no-gutters)
    v-col.pa-0(cols="12")
      v-btn(color="primary" @click="onBackToCalendarButtonClick")
        v-icon fa fa-chevron-double-left

  v-row(:style="getStyle" )
    v-col.pt-0(v-if="showCalendar" xs="12" sm="12" md="3" lg="3")
      Calendar.pa-0.ma-0(
        :isoWeek.sync="calendarIsoWeek" :menuOfferCollection="weeklyOfferCollection"
        :reservationCollection="weeklyReservationCollection" :date.sync="date"
      )
      v-skeleton-loader(v-if="fetchInProgress" type="paragraph@10")

    v-col(v-if="showOfferContent" xs="12" sm="12" md="9" lg="9")
      div.header
      TopGroup.pa-0(
        v-if="effectiveMenuGroupItem && date && !fetchInProgress" :setFreeDay="setFreeDay" :getEventName="getEventName" :business="business"
        :customer="customer"
        :menuGroupItem="effectiveMenuGroupItem" :date="date" :reservationItem="currentReservationItem" :boarder="boarder" :isAcceptanceProcess="isAcceptanceProcess" :settingsData="settingsData"
        @foodDelivered="foodWasDelivered" :menuGroupList="effectiveMenuGroups"
        @newReservationCreated="onNewReservationCreated" @reservationChanged="onReservationChanged"
        @selectionChange="onGroupSelectionChange(effectiveMenuGroupItem, ...arguments)"
        @timeBlockChanged="onTimeBlockChanged"
      )
      v-skeleton-loader(v-if="fetchInProgress" type="list-item-avatar-three-line, article, actions")


</template>

<script lang="ts">
import Vue from 'vue'
import moment from 'moment'
import { Component, Prop, Watch } from 'vue-property-decorator'
import TopGroup from '@/components/boarder-detail-dialog/TopGroup.vue'
import MenuReservationItem from '@/model/dailymenu/MenuReservationItem'
import DailyMenuOfferOrder from '@/model/dailymenu/DailyMenuOfferOrder'
import { SubGroupSelectionMap } from '@/types'
import type {
  BoarderResponse,
  OfferReservationResponse,
  OfferReservationResponseItem,
  StructuredMenuOfferResponseItem
} from '@/types/service-response'
import { FETCH_CALENDAR_EVENTS_FOR_DAY, FETCH_OFFER_RESERVATION, FETCH_TENANT_NAME } from '@/store/action-types'
import {
  FetchOfferReservationPayload,
  FetchStructuredMenuOfferPayload,
  FetchStructuredMenuOfferWithCancelTokenPayload,
  FilterEventsPayload
} from '@/types/payload'
import VueI18n from 'vue-i18n'
import { CurrentUser } from '@/model/CurrentUser'
import { USER_LOGGED_IN } from '@/store/getter-types'
import Calendar from '@/components/boarder-detail-dialog/Calendar.vue'
import MenuGroupItemOrder from '@/model/dailymenu/MenuGroupItemOrder'
import CalendarEventForDay from '@/model/calendar/CalendarEventForDay'
import { CalendarEvent } from 'vuetify'
import { Action } from 'vuex-class'
import axios, { CancelTokenSource } from 'axios'
import BusinessModel from '@/model/menu/BusinessModel'
import TenantName from '@/model/tennant/TenantName'
import _ from 'lodash'
import { usePiniaStore } from '@/store/piniaStore'
import { useBoarderFoodOrderStore } from '@/store/boarderFoodOrderStore'
import TranslateResult = VueI18n.TranslateResult
import BusinessTimeBlockCollection from '@/model/business/BusinessTimeBlockCollection'
import Configuration from '@/model/Configuration'

@Component({
  components: {
    TopGroup,
    Calendar
  }
})
export default class OrdersTab extends Vue {

  @Prop({type: Object, required: false}) readonly boarder!: BoarderResponse
  @Prop({type: Array as () => BusinessModel[]}) business!: BusinessModel[]
  @Prop({type: Array }) settingsData!: any[]
  @Prop({type: Object, required: true}) readonly customer!: any


  @Action(FETCH_CALENDAR_EVENTS_FOR_DAY) fetchCalendarEventsForDay!: { (payload: FilterEventsPayload): Promise<CalendarEventForDay[]> }
  @Action(FETCH_TENANT_NAME) fetchSettingsData!: { (): Promise<TenantName[]> }

  private piniaStore = usePiniaStore()
  private boarderFoodOrderStore = useBoarderFoodOrderStore()

  private menuGroupItem: any | null = null
  private showSnackBar: boolean = false
  private date: Date = new Date
  private isoWeek: string = moment(new Date).format('GGGG[W]WW')
  private calendarIsoWeek: string = this.isoWeek
  private weeklyOfferCollection:  DailyMenuOfferOrder[] | null = null
  private weeklyReservationCollection: any[] | null = []
  private snackbarColor: string = 'success'
  private snackBarMessage: TranslateResult | null = null
  private currentSelection: any = new Map()

  private events: CalendarEventForDay[] | null = null
  private setFreeDay: boolean = false

  private fetchInProgress: boolean = false
  private cancelToken: CancelTokenSource | null = null
  private menuGroupList: any[] | null = null
  private showMobileCalendar : boolean = true
  private selectedTimeBlock: BusinessTimeBlockCollection | null = null

  get isAcceptanceProcess(): boolean {
    let condition: boolean
    const foundItem: any = this.settingsData.find((c: any) => c.key ===  'tenant.allow_acceptance_process')
    if(foundItem){
      condition = foundItem.value === 'true'
    } else {
      condition = false
    }

    return condition
  }

  get getStyle (): string {
    if(this.$vuetify.breakpoint.smAndDown) {
      return ""
    } else {
      return "height: 450px; overflow-y: scroll"
    }
  }

  get currentUser (): CurrentUser {
    return this.$store.getters[USER_LOGGED_IN]
  }

  get showMobileNavigationBar(): boolean {
    return this.$vuetify.breakpoint.smAndDown && this.showOfferContent
  }

  get showCalendar (): boolean {
    if (this.$vuetify.breakpoint.smAndDown) {
      return this.showMobileCalendar
    }

    return true
  }

  get currentReservationItem (): any[] | undefined {
    if (this.date && this.weeklyReservationCollection) {
      return this.weeklyReservationCollection.filter((item: any) => moment(item.date).isSame(this.date, 'date'))
    }
  }

  get showOfferContent (): boolean {
    if (this.$vuetify.breakpoint.smAndDown) {
      return !this.showMobileCalendar
    }
    return true
  }

  get effectiveMenuGroups(): MenuGroupItemOrder[] {
    if (this.selectedTimeBlock && this.boarderFoodOrderStore.areTimeBlockLinkedToGroups) {
      let menuGroupIds: Number[] = []

      this.boarderFoodOrderStore.configuration.forEach((c: Configuration) => {
        let ids = (c.settings as any)?.time_block_menu_group_link[this.selectedTimeBlock!.id] || []

        menuGroupIds = menuGroupIds.concat(ids)
      })

      menuGroupIds = _.uniq(menuGroupIds)

      return  this.menuGroupList!.filter((m: MenuGroupItemOrder) => menuGroupIds.includes((m.id as any)))
    } else {
      return this.menuGroupList || []
    }
  }

  get effectiveMenuGroupItem(): MenuGroupItemOrder | null {
    return this.effectiveMenuGroups[0]
  }

  getCurrentEvent() {
    const foundEvent: CalendarEvent | undefined = this.events!.find((item: CalendarEvent) => item.date === moment(this.date).format('YYYY-MM-DD'))
    this.setFreeDay = !!foundEvent;
  }

  get getEventName(): string {
    if (this.events) {
      const foundEvent: CalendarEventForDay | undefined = this.events!.find((item: CalendarEventForDay) => moment(item.date).format('D.M.YYYY') == moment(this.date).format('D.M.YYYY'))
      if (foundEvent && foundEvent.kind.name == 'holiday') {
        return this.$t('dailymenuPage.freeDayInformationText', {eventName: 'sú prázdniny'}).toString()
      } else if (foundEvent && foundEvent.kind.name == 'free_day') {
        return this.$t('dailymenuPage.freeDayInformationText', {eventName: 'je voľno'}).toString()
      }
    }

    return ''
  }

  @Watch('isoWeek')
  test1 (newVal: string): void {
    this.fetchWrapper([
      () => this.fetchWeeklyData(newVal),
    ])
  }

  @Watch('date')
  test2 (newVal: Date): void {console.debug(newVal)
    const dateOffer = this.findCachedOfferForDate(newVal)

    if (dateOffer) {
      // FIXME: predpoklada sa ze vzdy bude zobrazeny obed
      //this.menuGroupItem = dateOffer.menuGroupList[0]
      this.menuGroupList = dateOffer.menuGroupList
    }

    if (this.events) {
      this.getCurrentEvent()
    }
    this.showMobileCalendar = false
  }

  @Watch('calendarIsoWeek')
   test5 (newVal: string): void {
    if (newVal) {
          this.isoWeek = newVal
    }
  }

  @Watch('weeklyOfferCollection')
  test3 (newVal: DailyMenuOfferOrder[] | undefined): void {
    if (newVal && newVal.length) {
      const currentDateOffer = this.findCachedOfferForDate(this.date as unknown as Date)

      if (currentDateOffer) {
        // FIXME: predpoklada sa ze vzdy bude zobrazeny obed
        //this.menuGroupItem = currentDateOffer.menuGroupList[0]
        this.menuGroupList = currentDateOffer.menuGroupList
      }
    }
    this.showMobileCalendar = true
  }

  @Watch('showOfferContent')
  test4 (newVal: any): void {
    if (newVal && !this.date) {
      this.$router.replace({
        name: 'Order',
        params: {
          isoWeek: this.isoWeek,
          date: moment(this.isoWeek).startOf('isoWeek').format('GGGG-MM-DD')
        }
      }).catch((e) => console.debug(e))
    }
  }

  foodWasDelivered(): void {
    this.snackbarColor = 'success'
    this.snackBarMessage = this.$t('orderPage.adminDeliverFoodSuccessMessage')
    this.showSnackBar = true

    this.fetchWeeklyData(this.isoWeek)
  }

  onBackToCalendarButtonClick (): void {
    this.showMobileCalendar = true
  }

  findCachedOfferForDate (date: Date): DailyMenuOfferOrder | undefined {
    if (this.weeklyOfferCollection) {
      return this.weeklyOfferCollection.find((i: DailyMenuOfferOrder) => moment(i.date).isSame(date, 'date'))
    }
  }

  async fetchEventsData(start: Date, end: Date): Promise<void> {

    let payload: FilterEventsPayload = {
      before: end,
      after: start
    }

    try {
      if(this.weeklyOfferCollection) {
        let response: CalendarEventForDay[] | null = await this.fetchCalendarEventsForDay(payload)
        this.events = response.map((e): CalendarEventForDay => ({
          id: e.id,
          kind: e.kind,
          date: moment(e.date).format('YYYY-MM-DD'),
        }))
      }

    } catch (e) {
      this.showSnackBar = true
      this.snackbarColor = 'error'
      this.snackBarMessage = this.$t('errors.fetchDataError').toString()
    } finally {
    }
  }

  async fetchWeeklyData (isoWeek: string): Promise<void> {
    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = axios.CancelToken.source()

    const startDate = moment(isoWeek).startOf('isoWeek').toDate()
    const endDate = moment(isoWeek).endOf('isoWeek').toDate()

    const promises = [
      this.fetchWeeklyOffer(startDate, endDate),
      this.fetchWeeklyReservation(startDate, endDate),
      this.fetchEventsData(startDate, endDate)
    ]

    const [
      offerCollection,
      reservationCollection
    ] = await Promise.all(promises as Promise<Object[]>[]) as [DailyMenuOfferOrder[], MenuReservationItem[], CalendarEventForDay[]]
    this.weeklyOfferCollection = offerCollection
    this.weeklyReservationCollection = reservationCollection

    this.fixDateIfNeeded()
  }

  async fetchWeeklyOffer (dateFrom: Date, dateTo: Date): Promise<DailyMenuOfferOrder[]> {
    this.weeklyOfferCollection = null

    let cancelToken = this.cancelToken

    let payloadWithoutCancelToken: FetchStructuredMenuOfferPayload = {
      tenantCode: this.currentUser.tenant.code,
      dateFrom,
      dateTo,
      business: this.customer.businesses[0]
    }

    const response = await this.piniaStore.fetchStructuredMenuOffer({
      cancelToken: cancelToken,
      payload: payloadWithoutCancelToken
    } as FetchStructuredMenuOfferWithCancelTokenPayload)

    return response
        .map((item: StructuredMenuOfferResponseItem) => DailyMenuOfferOrder.createFromResponse(item))
  }

  async fetchWeeklyReservation (dateFrom: Date, dateTo: Date): Promise<MenuReservationItem[]> {
    this.weeklyReservationCollection = null

    const response: OfferReservationResponse = await this.$store.dispatch(FETCH_OFFER_RESERVATION, {
      tenantCode: this.currentUser.tenant.code,
      dateFrom,
      dateTo,
      boarderId: this.boarder.id
    } as FetchOfferReservationPayload)

    return response.map((item: OfferReservationResponseItem) => MenuReservationItem.createFromResponse(item))
  }

  async fetchConfiguration():Promise<void>{
    const payload: any = this.customer.businesses[0].id

    try {
      this.boarderFoodOrderStore.configuration = await this.piniaStore.fetchConfigurationGroups(payload)
    } catch (e) {
      this.showSnackBar = true
      this.snackbarColor = 'error'
      this.snackBarMessage = this.$t('errors.fetchDataError').toString()
    }
  }

  onNewReservationCreated (reservationItem: MenuReservationItem): void {

    if (this.weeklyReservationCollection) {
      this.weeklyReservationCollection.push(reservationItem)

      this.snackbarColor = 'success'
      this.snackBarMessage = this.$t('orderPage.newReservationCreatedMessage')
      this.showSnackBar = true
    }
  }

  onReservationChanged (changedItem: MenuReservationItem, stateResult: string): void {
    if (this.weeklyReservationCollection) {
      _.remove(this.weeklyReservationCollection, (item: MenuReservationItem) => item.reservationId === changedItem.reservationId)
      this.weeklyReservationCollection.push(changedItem)

      if (stateResult === 'canceled') {
        this.snackBarMessage = this.$t('orderPage.reservationCanceledMessage')
      } else if (stateResult === 'restored') {
        this.snackBarMessage = this.$t('orderPage.reservationRestoredMessage')
      } else {
        this.snackBarMessage = this.$t('orderPage.reservationChangedMessage')
      }

      this.snackbarColor = 'success'

      this.showSnackBar = true
    }
  }

  onGroupSelectionChange (groupItem: MenuGroupItemOrder, selection: SubGroupSelectionMap): void {
    this.currentSelection.set(groupItem, selection)
  }

  onTimeBlockChanged (timeBlock: BusinessTimeBlockCollection): void {
    this.selectedTimeBlock = timeBlock
  }

  fixDateIfNeeded (): void {
    if (this.weeklyOfferCollection && this.date) {
      const offerDateCollection = this.weeklyOfferCollection.map((item: DailyMenuOfferOrder) => item.date)
      this.date = new Date(moment(_.first(offerDateCollection)).format('YYYY-MM-DD'))
    }
  }

  async fetchData (): Promise<void> {
    const payload: any = this.customer.businesses[0].id
    try {
      this.boarderFoodOrderStore.timeBlocks = await this.piniaStore.fetchTimeBlocks(payload)
    }catch (e) {
    }
  }

  async fetchWrapper(functions: any[]): Promise<any> {
    this.fetchInProgress = true

    try {
      return await Promise.all(functions.map(fn => fn()));
    } finally {
      this.fetchInProgress = false;
    }
  }



  mounted() {
    this.boarderFoodOrderStore.$reset()

    this.fetchWrapper([
      () => this.fetchWeeklyData(this.isoWeek),
      () => this.fetchData(),
      () => this.fetchConfiguration()
    ])
  }
}

</script>

<style lang="stylus" scoped>

</style>
